/*
 * TM Cart-Aware Quantity Stepper
 * -------------------------------
 * Replaces the product card's quantity widget in the Supermarket Grid (and the
 * Quick View) with a stepper that reflects how many of that product are CURRENTLY
 * in the cart.
 *
 * Behaviour:
 *  - On load (and after any grid swap / cart change) we read /cart.js and set
 *    each stepper's displayed number to the quantity of that variant in the cart
 *    (0 if not in the cart).
 *  - +/- adjust the displayed (pending) number locally.
 *  - The card's existing "Add to basket" submits this number. We intercept the
 *    submit to SET the cart line to the shown quantity (not add-on-top), using
 *    /cart/change when the line exists, or /cart/add when it doesn't.
 *  - After the cart updates, every stepper for that variant re-syncs so all
 *    cards (and quick view) show the same number, and the cart bubble updates.
 *
 * Markup contract (rendered by snippets/tm-cart-stepper.liquid):
 *   <div class="tm-cart-stepper" data-variant-id="123" data-product-id="9">
 *     <button class="tm-cs-minus">-</button>
 *     <input class="tm-cs-input" name="quantity" value="0" />
 *     <button class="tm-cs-plus">+</button>
 *   </div>
 * The stepper sits INSIDE the product form so the theme's add-to-cart reads its
 * value via the `quantity` field. We additionally drive the cart directly so the
 * "set" semantics hold even when the item is already in the cart.
 */
(function () {
  'use strict';

  var LOG = '[TM-cart-stepper]';
  var cartIndex = {};          // variantId -> quantity in cart
  var cartLineKeys = {};       // variantId -> line item key (for /cart/change)
  var _syncing = false;
  var _refreshTimer = null;

  function log() {
    if (window.tmCartStepperDebug) console.log.apply(console, [LOG].concat([].slice.call(arguments)));
  }

  // ---- Cart state ---------------------------------------------------------

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartIndex = {};
        cartLineKeys = {};
        (cart.items || []).forEach(function (item) {
          cartIndex[item.variant_id] = (cartIndex[item.variant_id] || 0) + item.quantity;
          cartLineKeys[item.variant_id] = item.key;
        });
        log('cart synced', cartIndex);
        return cart;
      })
      .catch(function (e) { console.error(LOG, 'cart fetch failed', e); return null; });
  }

  // Set every stepper on the page to the cart quantity for its variant.
  function applyCartToSteppers(root) {
    var scope = root || document;
    scope.querySelectorAll('.tm-cart-stepper').forEach(function (stepper) {
      var vid = stepper.dataset.variantId;
      var qty = cartIndex[vid] || 0;
      setStepperValue(stepper, qty, /*silent*/ true);
      stepper.classList.toggle('tm-cs-in-cart', qty > 0);
    });
  }

  function getInput(stepper) { return stepper.querySelector('.tm-cs-input'); }

  function setStepperValue(stepper, value, silent) {
    var input = getInput(stepper);
    if (!input) return;
    value = Math.max(0, parseInt(value, 10) || 0);
    input.value = value;
    // Reflect "in cart" count for the label, if present
    var countEl = stepper.querySelector('.tm-cs-count');
    if (countEl) {
      var inCart = cartIndex[stepper.dataset.variantId] || 0;
      countEl.textContent = inCart > 0 ? (inCart + ' in basket') : '';
      countEl.classList.toggle('hidden', inCart <= 0);
    }
    if (!silent) log('set value', stepper.dataset.variantId, value);
  }

  // ---- Cart mutations -----------------------------------------------------

  // SET the cart line for a variant to `qty`. Uses /cart/change when the line
  // exists (including qty 0 to remove); /cart/add when it's not in the cart.
  function setCartQuantity(variantId, qty) {
    qty = Math.max(0, parseInt(qty, 10) || 0);
    var inCart = cartIndex[variantId] || 0;
    var req;

    if (inCart > 0) {
      // change by line key (robust against multiple line items / properties)
      var key = cartLineKeys[variantId];
      req = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: qty })
      });
    } else if (qty > 0) {
      req = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
      });
    } else {
      return Promise.resolve(); // nothing in cart, asked for 0 → no-op
    }

    return req
      .then(function (r) { return r.json(); })
      .then(function () { return fetchCart(); })
      .then(function () {
        applyCartToSteppers();
        refreshCartUI();
      })
      .catch(function (e) { console.error(LOG, 'setCartQuantity failed', e); });
  }

  // Update the theme's cart bubble + drawer by re-fetching the relevant
  // sections (mirrors what the theme's own add-to-cart does).
  function refreshCartUI() {
    var ids = ['cart-icon-bubble'];
    // include cart drawer if present
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    var url = '?sections=' + ids.join(',');
    fetch(window.location.pathname + url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        // cart bubble
        if (sections['cart-icon-bubble']) {
          var bubbleHost = document.getElementById('cart-icon-bubble');
          if (bubbleHost) {
            var doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            var fresh = doc.getElementById('cart-icon-bubble');
            if (fresh) bubbleHost.innerHTML = fresh.innerHTML;
          }
        }
        // cart drawer body
        if (sections['cart-drawer']) {
          var drawer = document.getElementById('CartDrawer');
          if (drawer) {
            var ddoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            var freshDrawer = ddoc.getElementById('CartDrawer');
            if (freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;
          }
        }
        // Notify the theme so its own cart UI (drawer contents, count helpers)
        // updates without a page reload.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('maximize:cart:updated', { bubbles: true, detail: {} }));
        if (window.xCartHelper && typeof window.xCartHelper.updateCurrentCountItem === 'function') {
          try { window.xCartHelper.updateCurrentCountItem(); } catch (e) {}
        }
      })
      .catch(function (e) { log('cart UI refresh failed (non-fatal)', e); });
  }

  // Fire the theme's fly-to-cart animation from a source element.
  function fireFlyToCart(sourceEl) {
    try {
      document.dispatchEvent(new CustomEvent('tm:fly-to-cart', {
        bubbles: true,
        detail: { trigger: sourceEl }
      }));
    } catch (e) {}
  }

  // ---- Event handling (delegated, survives AJAX swaps) --------------------

  function onClick(e) {
    var minus = e.target.closest('.tm-cs-minus');
    var plus = e.target.closest('.tm-cs-plus');
    var addBtn = e.target.closest('.tm-cs-add');
    if (!minus && !plus && !addBtn) return;

    var stepper = e.target.closest('.tm-cart-stepper');
    if (!stepper) return;
    e.preventDefault();

    var input = getInput(stepper);
    var current = parseInt(input.value, 10) || 0;
    var variantId = stepper.dataset.variantId;

    var isQuickView = stepper.dataset.context === 'quickview';

    if (minus) {
      var next = Math.max(0, current - 1);
      setStepperValue(stepper, next);
      // Card: if the item is in the cart, minus updates the cart live. Quick
      // view: never touch the cart here — the native add button submits the
      // chosen quantity, so adjusting here must not also add/change the cart
      // (that caused the "adds 2 at a time" double-add).
      if (!isQuickView && (cartIndex[variantId] || 0) > 0) setCartQuantity(variantId, next);
    } else if (plus) {
      var up = current + 1;
      setStepperValue(stepper, up);
      if (!isQuickView && (cartIndex[variantId] || 0) > 0) setCartQuantity(variantId, up);
    } else if (addBtn) {
      // Explicit add (card only): set the cart to the shown quantity (min 1).
      var qty = current < 1 ? 1 : current;
      setStepperValue(stepper, qty);
      // Fly-to-cart animation from this card's product image, if present.
      var card = addBtn.closest('.supermarket-grid__item') || addBtn.closest('.product-card') || stepper;
      var imgEl = (card && card.querySelector) ? (card.querySelector('img') || addBtn) : addBtn;
      fireFlyToCart(imgEl);
      setCartQuantity(variantId, qty);
      flash(addBtn);
    }
  }

  function flash(btn) {
    btn.classList.add('tm-cs-added');
    setTimeout(function () { btn.classList.remove('tm-cs-added'); }, 1200);
  }

  // ---- Init / re-sync -----------------------------------------------------

  function scheduleResync(root) {
    clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(function () {
      fetchCart().then(function () { applyCartToSteppers(root); });
    }, 50);
  }

  function init() {
    document.addEventListener('click', onClick);

    // Re-sync after our grid swaps (tab / subcategory / search / filter)
    document.addEventListener('tm:supermarket-grid:content-swapped', function (ev) {
      var root = (ev.detail && ev.detail.root) ? ev.detail.root : document;
      scheduleResync(root);
    });

    // Re-sync when the theme reports a cart change from elsewhere
    document.addEventListener('cart:refresh', function () { if (!_syncing) scheduleResync(document); });
    // The theme dispatches this after its OWN add-to-cart (incl. quick view's
    // native button). Re-sync so home-page cards reflect the new quantity
    // without a reload.
    document.addEventListener('maximize:cart:updated', function () { scheduleResync(document); });

    // Quick view (and other dynamic content) injects steppers without firing a
    // reliable event, so watch the DOM: whenever a new .tm-cart-stepper appears,
    // re-fetch the cart and sync all steppers so the quick-view one shows the
    // correct current cart quantity immediately.
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function (mutations) {
        var sawStepper = false;
        mutations.forEach(function (m) {
          m.addedNodes && m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.classList && node.classList.contains('tm-cart-stepper')) sawStepper = true;
            else if (node.querySelector && node.querySelector('.tm-cart-stepper')) sawStepper = true;
          });
        });
        if (sawStepper) scheduleResync(document);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    fetchCart().then(function () { applyCartToSteppers(document); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.tmCartStepper = { fetchCart: fetchCart, apply: applyCartToSteppers, set: setCartQuantity };
})();
